import { c as _c } from "react/compiler-runtime";
import { useEffect, type RefObject } from "react";

/**
 * Pauses an autoplaying video while it is off-screen or its tab is in the
 * background, and resumes it when it becomes visible again.
 *
 * `useNearViewport` is a one-shot latch, so without this a looping background
 * video keeps decoding frames for the whole visit — and keeps invalidating every
 * `backdrop-filter` layered over it, the agent panel's blurred chrome included.
 *
 * It only ever resumes what it paused itself, so a visitor who pressed pause on
 * a video with controls stays paused.
 */
export function usePauseWhenInvisible(elementRef, enabled) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== elementRef || $[1] !== enabled) {
    t0 = () => {
      const element = elementRef.current;
      if (!element || !enabled) {
        return;
      }
      let hasAutoPaused = false;
      let isOnScreen = false;
      const pause = () => {
        if (element.paused) {
          return;
        }
        hasAutoPaused = true;
        element.pause();
      };
      const resume = () => {
        if (!hasAutoPaused) {
          return;
        }
        hasAutoPaused = false;
        element.play().catch(_temp);
      };
      const syncPlayback = () => {
        if (isOnScreen && !document.hidden) {
          resume();
        } else {
          pause();
        }
      };
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        isOnScreen = entry.isIntersecting;
        syncPlayback();
      });
      observer.observe(element);
      element.addEventListener("play", syncPlayback);
      document.addEventListener("visibilitychange", syncPlayback);
      return () => {
        observer.disconnect();
        element.removeEventListener("play", syncPlayback);
        document.removeEventListener("visibilitychange", syncPlayback);
      };
    };
    t1 = [elementRef, enabled];
    $[0] = elementRef;
    $[1] = enabled;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function _temp() {}